// apps/content/content/data/cities.ts

import type {
  City,
  CityTier,
  SectorContext,
  SerpDifficulty,
  UniqueLocalAngle,
} from "@/lib/content-types";
import {
  BERGAMO_SECTORS,
  TREVIGLIO_SECTORS,
  CARAVAGGIO_SECTORS,
  ROMANO_SECTORS,
} from "./sectors";

// Re-export types for backward compat
export type { City, CityTier, SectorContext, SerpDifficulty, UniqueLocalAngle };

export const HQ_CITY_SLUG = "bariano";

export const CITIES: City[] = [
  {
    slug: "bergamo",
    name: "Bergamo",
    province: "BG",
    postalCode: "24121",
    population: 122000,
    impreseAttive: 14200,
    distanceFromBarianoKm: 18,
    coordinates: { lat: 45.6949, lng: 9.6703 },
    dominantIndustries: [
      "servizi professionali",
      "fintech",
      "turismo",
      "retail",
      "salute",
      "tech",
    ],
    nearbyTowns: ["Dalmine", "Seriate", "Zanica", "Stezzano"],
    angleHook:
      "Competi con Milano senza spendere come Milano: performance e conversioni da capoluogo",
    proofSnippet:
      "Background enterprise (5 anni in Accenture su progetti Saipem e Nexi) e 8 recensioni clienti 5.0/5: porto alle imprese di Bergamo il rigore dei grandi progetti, senza i costi di un'agenzia",
    pricingVariance: 15,
    serpLocalPackDifficulty: "high",
    featured: true,
    tier: 1,
    sectors: BERGAMO_SECTORS,
    publishHub: true,
    publishCrossServices: [
      "siti-wordpress",
      "wordpress-performance",
      "ecommerce-woocommerce",
      "sviluppo-custom-nextjs",
      "mvp-saas",
      "bug-fixing-ai-nocode",
    ],
    uniqueLocalAngle: {
      paragraph:
        "Bergamo è capoluogo di una delle province più ricche d'Italia: 14.200 imprese attive distribuite tra manifatturiero export-driven (meccanica Val Seriana, tessile), servizi professionali B2B in Città Bassa, fintech e tech concentrati nel polo Kilometro Rosso. L'aeroporto di Bergamo-Orio al Serio è il terzo scalo italiano per traffico passeggeri e genera un indotto economico significativo per logistics, hospitality e servizi. La domanda digitale locale è sofisticata: il prospect bergamasco compara preventivi online prima di rispondere al telefono. Competi con Milano mantenendo costi sostenibili e relazione diretta: un sito ben posizionato intercetta su Google, qualifica il prospect con contenuti settoriali, converte senza intermediari.",
      keyEvent: "Kilometro Rosso, Bergamo Film Meeting, Fiera Campionaria Bergamo",
      competitiveContext:
        "Molte agenzie milanesi si contendono clienti Bergamo. Vantaggio founder-led locale: tariffe premium ma giustificate vs Milano, decisioni rapide, nessun account manager intermedio",
    },
  },
  {
    slug: "treviglio",
    name: "Treviglio",
    province: "BG",
    postalCode: "24047",
    population: 30500,
    impreseAttive: 3120,
    distanceFromBarianoKm: 8,
    coordinates: { lat: 45.5219, lng: 9.5937 },
    dominantIndustries: [
      "logistica",
      "retail",
      "metalmeccanica",
      "servizi",
      "commercio",
    ],
    nearbyTowns: ["Caravaggio", "Brignano Gera d'Adda", "Pontirolo Nuovo"],
    angleHook:
      "Tra Bergamo e Milano: il tuo sito posizionato su due province, due hub logistici",
    proofSnippet:
      "Preventivo in 48 ore, codice di tua proprietà su GitHub e un solo interlocutore dalla call iniziale alla messa online",
    pricingVariance: 10,
    serpLocalPackDifficulty: "med",
    featured: true,
    tier: 2,
    sectors: TREVIGLIO_SECTORS,
    publishHub: true,
    // Matrice giugno 2026 (docs/seo/keyword-map-2026-06.md): 4 cross con
    // sostanza locale reale. mvp-saas e bug-fixing si coprono dal master.
    publishCrossServices: [
      "siti-wordpress",
      "wordpress-performance",
      "ecommerce-woocommerce",
      "sviluppo-custom-nextjs",
    ],
    uniqueLocalAngle: {
      paragraph:
        "Treviglio è snodo logistico tra Bergamo, Milano e Cremona: 3.120 imprese attive con radici nel distretto metalmeccanico storico, retail commerciale denso e un polo logistico in espansione. La stazione ferroviaria di Treviglio è crocevia sulla linea Milano-Venezia e sulla direttrice per Brescia, con flussi giornalieri di pendolari che generano domanda retail e servizi di prossimità. Il posizionamento digitale qui non può ignorare la duplicità del bacino: BG a est, MI a ovest. Clientela diversificata: PMI metalmeccaniche B2B con esigenze di schede prodotto tecniche, retail B2C che cerca visibilità Maps, operatori logistici che servono la Lombardia orientale. Un approccio unico funziona poco: serve contenuto localizzato per entrambi i bacini.",
      competitiveContext:
        "Agenzie locali Treviglio poche e generaliste. Agenzie Milano costose. Opportunità posizionamento 'freelance premium locale' con tariffe Bergamo + qualità Milano",
    },
  },
  {
    slug: "caravaggio",
    name: "Caravaggio",
    province: "BG",
    postalCode: "24043",
    population: 17000,
    impreseAttive: 1450,
    distanceFromBarianoKm: 4,
    coordinates: { lat: 45.4969, lng: 9.6435 },
    dominantIndustries: [
      "turismo religioso",
      "agroalimentare",
      "commercio",
      "servizi",
    ],
    nearbyTowns: [
      "Bariano",
      "Mozzanica",
      "Pagazzano",
      "Treviglio",
      "Brignano Gera d'Adda",
    ],
    angleHook:
      "Dal Santuario al mondo: storytelling digitale che trasforma visitatori in clienti",
    proofSnippet:
      "Siti multilingua per ricettività e produttori locali: progetto, sviluppo e messa online seguiti in prima persona, senza passaggi di mano",
    pricingVariance: 0,
    serpLocalPackDifficulty: "med",
    featured: true,
    tier: 2,
    sectors: CARAVAGGIO_SECTORS,
    publishHub: true,
    // Matrice giugno 2026: 3 cross (turismo multilingua, vendita diretta
    // agroalimentare, performance). Il resto si copre dal master.
    publishCrossServices: [
      "siti-wordpress",
      "ecommerce-woocommerce",
      "wordpress-performance",
    ],
    uniqueLocalAngle: {
      paragraph:
        "Caravaggio è meta turistica religiosa di rilevanza internazionale grazie al Santuario della Beata Vergine di Caravaggio, polo di pellegrinaggio attivo tutto l'anno con picco nella Festa del 26 maggio. Il flusso include pellegrini sudamericani (via Spagna e Portogallo), tedeschi, inglesi e gruppi parrocchiali italiani: domanda multilingua reale, non teorica. Parallelamente: 1.450 imprese attive, forte settore agroalimentare (taleggio DOP, salumi artigianali), ricettività e commercio orientato ai visitatori. Il sito web qui opera su due fronti distinti: intercettare pellegrini con contenuti EN/DE/ES e prenotazioni integrate, e servire produttori locali che vogliono vendita diretta online verso consumatori italiani e turismo Bergamo.",
      keyEvent: "Festa della Madonna del Fonte (26 maggio), pellegrinaggi internazionali continui",
    },
  },
  {
    slug: "romano-di-lombardia",
    name: "Romano di Lombardia",
    province: "BG",
    postalCode: "24058",
    population: 20000,
    impreseAttive: 1780,
    distanceFromBarianoKm: 6,
    coordinates: { lat: 45.5183, lng: 9.7542 },
    dominantIndustries: ["tessile", "metalmeccanica", "commercio", "servizi"],
    nearbyTowns: ["Bariano", "Mozzanica", "Fornovo San Giovanni", "Martinengo"],
    angleHook:
      "Cuore commerciale della Bassa Bergamasca: il tuo sito deve reggere volumi e conversioni vere",
    proofSnippet:
      "Dalla scheda tecnica B2B al negozio online: un solo sviluppatore, requisiti scritti e consegne settimanali tracciate",
    pricingVariance: 5,
    serpLocalPackDifficulty: "med",
    featured: true,
    tier: 2,
    sectors: ROMANO_SECTORS,
    publishHub: true,
    // Matrice giugno 2026: 4 cross (tessile B2B showroom, commercio).
    publishCrossServices: [
      "siti-wordpress",
      "ecommerce-woocommerce",
      "sviluppo-custom-nextjs",
      "wordpress-performance",
    ],
    uniqueLocalAngle: {
      paragraph:
        "Romano di Lombardia è il centro commerciale naturale della Bassa Bergamasca: 1.780 imprese attive, distretto tessile storico con buyer internazionali (Europa, US) che si muovono su Milano Unica (la principale fiera italiana del tessile) e che qualificano fornitori bergamaschi online prima degli appuntamenti. Romano ha accesso rapido alla direttrice Treviglio-Brescia, il che ne fa snodo per distribuzione commerciale verso Brescia e Cremona. La domanda digitale è polarizzata: operatori tessili B2B high-end che cercano showroom virtuale e portfolio certificazioni, e PMI locali di commercio con budget ridotto che hanno bisogno di visibilità Maps efficiente senza overhead di agenzia. Strategie diverse, audience distinte, stesso territorio.",
    },
  },
  {
    slug: "bariano",
    name: "Bariano",
    province: "BG",
    postalCode: "24050",
    population: 4900,
    impreseAttive: 320,
    distanceFromBarianoKm: 0,
    coordinates: { lat: 45.5018, lng: 9.6678 },
    dominantIndustries: ["agricoltura", "metalmeccanica leggera", "piccolo commercio"],
    nearbyTowns: ["Morengo", "Mozzanica", "Fornovo San Giovanni", "Caravaggio"],
    angleHook:
      "Base operativa di Dimensione 4: servizi pensati per PMI della Bassa Bergamasca",
    proofSnippet:
      "Studio a Bariano: kick-off in persona per le aziende della zona e 8 recensioni clienti 5.0/5 su AddLance",
    pricingVariance: 0,
    serpLocalPackDifficulty: "low",
    featured: true,
    tier: 3,
    // Tier 3 (matrice giugno 2026): copertura via /comuni/bariano, niente
    // hub /zone né cross (anti-doorway, paesi <8k abitanti).
    publishHub: false,
    publishCrossServices: [],
    uniqueLocalAngle: {
      paragraph:
        "Bariano è la base operativa di Dimensione 4: 4.900 abitanti, 320 imprese attive, tessuto agricolo storico più metalmeccanica leggera e piccolo commercio. Essere qui significa conoscere direttamente i pattern delle PMI bergamasche, le dinamiche stagionali agricole, la rete di rapporti del territorio. Kick-off in persona gratuito per le aziende della zona. Rete professionale locale (fotografi, copywriter, designer) consolidata.",
    },
  },
  {
    slug: "morengo",
    name: "Morengo",
    province: "BG",
    postalCode: "24050",
    population: 2600,
    impreseAttive: 170,
    distanceFromBarianoKm: 3,
    coordinates: { lat: 45.4957, lng: 9.6343 },
    dominantIndustries: ["artigianato", "metalmeccanica", "agricoltura"],
    nearbyTowns: ["Bariano", "Romano di Lombardia", "Caravaggio"],
    angleHook:
      "Piccolo comune, ambizioni digitali da capoluogo: siti che ti fanno superare i limiti del territorio",
    proofSnippet:
      "Niente account manager: parli direttamente con chi sviluppa il tuo sito",
    pricingVariance: -5,
    serpLocalPackDifficulty: "low",
    featured: false,
    tier: 3,
    // Tier 3: solo /comuni/morengo (anti-doorway).
    publishHub: false,
    publishCrossServices: [],
    uniqueLocalAngle: {
      paragraph:
        "Morengo ha 2.600 abitanti e 170 imprese attive: tessuto economico tipico della Bassa Bergamasca con artigianato, metalmeccanica leggera, agricoltura. Founder-led diretto da Bariano: decisioni rapide, nessun account manager, prezzi senza maggiorazione.",
    },
  },
  {
    slug: "pagazzano",
    name: "Pagazzano",
    province: "BG",
    postalCode: "24040",
    population: 2100,
    impreseAttive: 130,
    distanceFromBarianoKm: 4,
    coordinates: { lat: 45.4943, lng: 9.6993 },
    dominantIndustries: ["agricoltura", "artigianato", "turismo locale"],
    nearbyTowns: ["Morengo", "Caravaggio", "Mozzanica"],
    angleHook:
      "Dal Castello Visconteo alle vetrine digitali: narrativa locale che converte visitatori in clienti",
    proofSnippet:
      "Siti pensati per attività locali: contenuti, foto e presenza Google Maps curati in prima persona",
    pricingVariance: -5,
    serpLocalPackDifficulty: "low",
    featured: false,
    tier: 3,
    publishHub: false,
    publishCrossServices: [],
  },
  {
    slug: "mozzanica",
    name: "Mozzanica",
    province: "BG",
    postalCode: "24050",
    population: 4700,
    impreseAttive: 390,
    distanceFromBarianoKm: 5,
    coordinates: { lat: 45.4597, lng: 9.6829 },
    dominantIndustries: ["plastiche", "stampaggio", "logistica", "agricoltura"],
    nearbyTowns: ["Bariano", "Pagazzano", "Caravaggio"],
    angleHook:
      "Sulla direttrice BG-CR-MI: visibilità online che non conosce confini provinciali",
    proofSnippet:
      "Per il B2B manifatturiero: schede prodotto tecniche, richieste preventivo tracciate e codice di tua proprietà",
    pricingVariance: -3,
    serpLocalPackDifficulty: "low",
    featured: false,
    tier: 3,
    // Tier 3: solo /comuni/mozzanica (anti-doorway).
    publishHub: false,
    publishCrossServices: [],
    uniqueLocalAngle: {
      paragraph:
        "Mozzanica è snodo sulla direttrice Bergamo-Cremona-Milano: 390 imprese attive con forte componente plastiche e stampaggio industriale. Il posizionamento digitale qui serve B2B manifatturiero che fornisce clientela diffusa in 3 province. Vicinanza a Caravaggio e Treviglio crea bacino di rapporti commerciali integrato.",
    },
  },
  {
    slug: "fornovo-san-giovanni",
    name: "Fornovo San Giovanni",
    province: "BG",
    postalCode: "24040",
    population: 3300,
    impreseAttive: 240,
    distanceFromBarianoKm: 3,
    coordinates: { lat: 45.5203, lng: 9.7069 },
    dominantIndustries: ["meccanica di precisione", "carpenteria", "agricoltura"],
    nearbyTowns: ["Romano di Lombardia", "Bariano", "Caravaggio"],
    angleHook:
      "Meccanica di precisione: lo stesso rigore nel codice del tuo sito",
    proofSnippet:
      "Rigore da meccanica di precisione anche nel codice: requisiti scritti, controlli automatici e consegne documentate",
    pricingVariance: -3,
    serpLocalPackDifficulty: "low",
    featured: false,
    tier: 3,
    // Tier 3: solo /comuni/fornovo-san-giovanni (anti-doorway).
    publishHub: false,
    publishCrossServices: [],
    uniqueLocalAngle: {
      paragraph:
        "Fornovo San Giovanni ha 240 imprese attive con forte specializzazione meccanica di precisione e carpenteria. Clientela B2B tecnica: chi cerca fornitori qui non improvvisa, valuta certificazioni ISO e tempi di consegna. Il sito web deve comunicare rigore e competenza tecnica, non vetrina commerciale generica.",
    },
  },
  {
    slug: "brignano-gera-d-adda",
    name: "Brignano Gera d'Adda",
    province: "BG",
    postalCode: "24053",
    population: 6500,
    impreseAttive: 560,
    distanceFromBarianoKm: 10,
    coordinates: { lat: 45.5367, lng: 9.5658 },
    dominantIndustries: ["agroalimentare", "logistica", "piccola industria"],
    nearbyTowns: ["Treviglio", "Caravaggio", "Fornovo San Giovanni"],
    angleHook:
      "Crocevia strategico Treviglio-Bergamo-Crema: il tuo sito come hub di conversione",
    proofSnippet:
      "Preventivatori e automazioni per logistica e agroalimentare: un solo interlocutore tecnico dal primo incontro alla messa online",
    pricingVariance: -2,
    serpLocalPackDifficulty: "low",
    featured: false,
    // Promosso a hub T2 (matrice giugno 2026): bacino Gera d'Adda con
    // domanda B2B reale, 3 cross con sostanza.
    tier: 2,
    publishHub: true,
    publishCrossServices: [
      "siti-wordpress",
      "ecommerce-woocommerce",
      "sviluppo-custom-nextjs",
    ],
    uniqueLocalAngle: {
      paragraph:
        "Brignano Gera d'Adda ha 6.500 abitanti e 560 imprese attive ed è uno snodo naturale tra Treviglio, Bergamo e Crema. Il tessuto economico è misto: agroalimentare con vendita diretta in crescita, logistica che serve la direttrice per Milano, piccola industria meccanica con clientela B2B distribuita su più province. La domanda digitale tipica qui è concreta: preventivatori online per gli operatori logistici, cataloghi e schede prodotto tecniche per la piccola industria, vendita diretta online per l'agroalimentare. Il borgo è noto per il Palazzo Visconti Citterio, tra le residenze storiche più rilevanti della Bassa, con un flusso di visite ed eventi che alimenta ricettività e commercio locale. Lavoro founder-led dallo studio di Bariano: un solo interlocutore tecnico, decisioni rapide, codice di proprietà del cliente.",
      keyEvent: "Palazzo Visconti Citterio, eventi e visite nella Gera d'Adda",
    },
  },
  {
    slug: "cologno-al-serio",
    name: "Cologno al Serio",
    province: "BG",
    postalCode: "24055",
    population: 10800,
    impreseAttive: 920,
    distanceFromBarianoKm: 11,
    coordinates: { lat: 45.5814, lng: 9.7081 },
    dominantIndustries: ["tessile", "metalmeccanica", "commercio", "agricoltura"],
    nearbyTowns: ["Urgnano", "Martinengo", "Romano di Lombardia"],
    angleHook:
      "Tradizione produttiva + tecnologia moderna: siti pensati per PMI manifatturiere",
    proofSnippet:
      "Cataloghi digitali per la manifattura: struttura tecnica curata e pannello di gestione contenuti semplice",
    pricingVariance: 3,
    serpLocalPackDifficulty: "low",
    featured: true,
    tier: 3,
    publishHub: false, // Deferred: substance check needed
    publishCrossServices: [],
  },
];

// ─── Retired city redirects (301 target: nearest active tier 1/2) ───────────

export const RETIRED_CITY_REDIRECTS: Record<string, string> = {
  dalmine: "bergamo",
  seriate: "bergamo",
  "osio-sotto": "treviglio",
  urgnano: "bergamo", // Hinterland BG: redirect a capoluogo
};

// ─── Helpers ────────────────────────────────────────────────────────────────

export function getCity(slug: string): City | undefined {
  return CITIES.find((c) => c.slug === slug);
}

export function getCityByName(name: string): City | undefined {
  const norm = name.trim().toLowerCase();
  return CITIES.find((c) => c.name.toLowerCase() === norm);
}

export function getFeaturedCities(): City[] {
  return CITIES.filter((c) => c.featured);
}

export function getPublishedHubCities(): City[] {
  return CITIES.filter((c) => c.publishHub);
}

export function getCitiesByTier(tier: CityTier): City[] {
  return CITIES.filter((c) => c.tier === tier);
}

export function citySize(city: City): "xs" | "s" | "m" | "l" {
  if (city.population < 3000) return "xs";
  if (city.population < 8000) return "s";
  if (city.population < 25000) return "m";
  return "l";
}
